// =============================================================================
// CfPagesTab — zakladka "Pages" w /cloudflare: lista projektow Cloudflare Pages
// per konto + usuwanie (porzadki po testach migracji). Projekt uzywany przez
// migracje (static_sites.pages_project_name) jest oznaczony; po skasowaniu
// panel czysci jego powiazanie, zeby nastepny deploy zalozyl projekt od nowa.
// Usuniecie = AlertDialog z przepisaniem nazwy projektu.
// =============================================================================

import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, RefreshCw, Trash2, ExternalLink, Layers } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import { listAccounts } from "@/services/cloudflareService";
import { listPagesProjects, deletePagesProject, type CfPagesProject } from "@/services/staticSite/cfPagesApi";

interface SiteLink {
  id: string;
  domain: string;
  status: string;
}

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export const CfPagesTab = ({ accounts, loading }: { accounts: CloudflareAccount[]; loading: boolean }) => {
  const { toast } = useToast();
  const [accId, setAccId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [projects, setProjects] = useState<CfPagesProject[]>([]);
  const [links, setLinks] = useState<Record<string, SiteLink>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const acc = accounts.find((a) => a.id === accId);

  const load = useCallback(async () => {
    if (!acc) return;
    setBusy(true);
    setError(null);
    try {
      let id = acc.account_id || "";
      if (!/^[0-9a-f]{32}$/i.test(id)) {
        const list = await listAccounts(acc.api_token);
        id = list[0]?.id || "";
        if (!id) throw new Error("Token nie widzi żadnego konta (brak Account Settings: Read)");
      }
      setAccountId(id);
      const [list, sitesRes] = await Promise.all([
        listPagesProjects(acc.api_token, id),
        (supabase as any).from("static_sites").select("id, domain, status, pages_project_name").not("pages_project_name", "is", null),
      ]);
      const sites = (sitesRes?.data || []) as Array<SiteLink & { pages_project_name: string }>;
      setProjects([...list].sort((a, b) => (b.created_on || "").localeCompare(a.created_on || "")));
      const map: Record<string, SiteLink> = {};
      for (const s of sites) map[s.pages_project_name] = { id: s.id, domain: s.domain, status: s.status };
      setLinks(map);
    } catch (e) {
      setError(errMsg(e));
      setProjects([]);
    } finally {
      setBusy(false);
    }
  }, [acc]);

  useEffect(() => { void load(); }, [load]);

  const remove = async (p: CfPagesProject) => {
    if (!acc || !accountId) return;
    setDeleting(p.name);
    try {
      await deletePagesProject(acc.api_token, accountId, p.name);
      const link = links[p.name];
      if (link) {
        await (supabase as any).from("static_sites").update({
          pages_project_name: null, pages_subdomain: null, pages_cf_account_id: null, pages_cf_account_remote_id: null,
          status: link.status === "live" ? "rolled_back" : "analyzed", asset_manifest: {}, updated_at: new Date().toISOString(),
        }).eq("id", link.id);
      }
      toast({ title: "Projekt usunięty", description: `${p.name}${link ? ` (migracja ${link.domain} odpięta)` : ""}` });
      setConfirm("");
      await load();
    } catch (e) {
      toast({ title: "Nie udało się usunąć", description: errMsg(e), variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="min-w-64 space-y-1.5">
            <div className="text-xs font-medium">Konto Cloudflare</div>
            <Select value={accId} onValueChange={setAccId} disabled={loading || !accounts.length}>
              <SelectTrigger><SelectValue placeholder={loading ? "Wczytuję…" : accounts.length ? "Wybierz konto" : "Brak kont"} /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}{a.email ? ` · ${a.email}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => void load()} disabled={!acc || busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Odśwież
          </Button>
          {acc && !busy && <Badge variant="secondary" className="ml-auto">{projects.length}/100 projektów</Badge>}
        </CardContent>
      </Card>

      {error && <Card className="border-destructive/40"><CardContent className="py-3 text-sm text-destructive">{error}</CardContent></Card>}

      {acc && !busy && !error && projects.length === 0 && (
        <Card><CardContent className="py-6 text-sm text-muted-foreground">To konto nie ma żadnych projektów Pages.</CardContent></Card>
      )}

      {projects.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y">
              {projects.map((p) => {
                const link = links[p.name];
                const url = p.subdomain ? `https://${p.subdomain}` : null;
                const customDomains = (p.domains || []).filter((d) => !d.endsWith(".pages.dev"));
                return (
                  <li key={p.id || p.name} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono font-medium">{p.name}</span>
                        {link ? (
                          <Badge variant="default" className="text-[10px]">migracja {link.domain} · {link.status}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">nieużywany przez panel</Badge>
                        )}
                        {customDomains.length > 0 && <Badge variant="secondary" className="text-[10px]">domena: {customDomains.join(", ")}</Badge>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {url && <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">{p.subdomain} <ExternalLink className="h-3 w-3" /></a>}
                        {p.created_on && <span>utworzony {new Date(p.created_on).toLocaleString("pl-PL")}</span>}
                      </div>
                    </div>
                    <AlertDialog onOpenChange={(o) => { if (!o) setConfirm(""); }}>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" disabled={deleting !== null}>
                          {deleting === p.name ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Usunąć projekt {p.name}?</AlertDialogTitle>
                          <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm">
                              <p>Cloudflare skasuje projekt razem ze wszystkimi deploymentami i podpiętymi domenami. Adres <span className="font-mono">{p.subdomain}</span> przestanie działać natychmiast. Tego nie da się cofnąć.</p>
                              {link && (
                                <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
                                  Ten projekt jest podpięty pod migrację <span className="font-mono">{link.domain}</span> (status {link.status}). Panel odepnie go od migracji, następne „Przenieś" założy nowy projekt.
                                  {link.status === "live" && <span className="font-medium"> Uwaga: strona jest LIVE na domenie. Po usunięciu domena przestanie działać!</span>}
                                </p>
                              )}
                              <p>Przepisz nazwę projektu, żeby potwierdzić:</p>
                              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={p.name} className="font-mono text-xs" autoFocus />
                            </div>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Anuluj</AlertDialogCancel>
                          <AlertDialogAction
                            disabled={confirm.trim() !== p.name}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => void remove(p)}
                          >
                            Usuń projekt
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
